import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { CommonModule } from '@angular/common';
import { AppHeightInputComponent } from 'src/app/core/components/app-height-input/height-input.component';
import { NoNegativeInputDirective } from 'src/app/core/directives/noNegativeInput.directive';
import { MaterialModule } from 'src/app/core/material.module';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { ConversionLengthService } from 'src/app/core/services/conversion/conversion-length.service';
import { AppCustomTextSelectComponent } from 'src/app/features/custom-texts/custom-text-select/custom-text-select.component';
import { PatientsService } from 'src/app/features/patients/services/patients.service';
import {
  ALL_ALCOHOL_OPTIONS,
  ALL_CAFFEINE_OPTIONS,
  ALL_EXERCISE_OPTIONS,
  ALL_MEDICATION_GROUPS_OPTIONS,
  ALL_RACE_OPTIONS,
  ALL_STRESS_LEVEL_OPTIONS,
  BOOLEAN_CHOOSE,
} from 'src/app/features/tests/features/constants';
import { UsersSelectComponent } from 'src/app/features/user-management/components/users-select/users-select.component';
import { AppEditorComponent } from '../../../../../../core/components/app-editor/app-editor.component';
import { ReferringDoctorSelectComponent } from '../../../../../referring-doctors/components/referring-doctors-select/referring-doctors-select.component';
import {
  PatientShippingAddressComponent,
  shippingAddressFormValue,
  shippingAddressGroup,
  shippingAddressPayload,
} from '../../../patient-shipping-address/patient-shipping-address.component';
import { PatientType } from 'src/app/features/dormotech-direct/dormotech-direct.types';

@Component({
  selector: 'app-patient-add-info',
  templateUrl: './patient-add-info.component.html',
  styleUrls: ['./patient-add-info.component.scss'],
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    AppEditorComponent,
    ReferringDoctorSelectComponent,
    UsersSelectComponent,
    AppCustomTextSelectComponent,
    PatientShippingAddressComponent,
    NoNegativeInputDirective,
    AppHeightInputComponent,
  ],
})
export class PatientAddInfoComponent implements OnInit, OnChanges {
  @ViewChild(PatientShippingAddressComponent) private shippingAddress?: PatientShippingAddressComponent;
  @Output() additionalFormValue = new EventEmitter();
  @Input() patientData: any;
  /**
   * A Direct patient's device is shipped by Dormo Direct, so its address is the
   * US one the Direct lab validates, and the step cannot be left without it.
   */
  @Input() patientType: PatientType = 'CLINIC';
  public editable: boolean = false;
  constructor(
    public ability: AbilityService,
    public patientService: PatientsService,
    public conversionLengthService: ConversionLengthService
  ) {
    this.patientService.eventEmitter.subscribe((d) => {
      if (d == 'app-patient-add-info') {
        this.sendData();
      }
      if (d == 'reset') {
        this.patientDetailAddInfo.reset();
      }
    });
  }
  ALL_RACE_OPTIONS = ALL_RACE_OPTIONS;
  ALL_ALCOHOL_OPTIONS = ALL_ALCOHOL_OPTIONS;
  ALL_CAFFEINE_OPTIONS = ALL_CAFFEINE_OPTIONS;
  ALL_EXERCISE_OPTIONS = ALL_EXERCISE_OPTIONS;
  ALL_STRESS_LEVEL_OPTIONS = ALL_STRESS_LEVEL_OPTIONS;
  ALL_MEDICATION_GROUPS_OPTIONS = ALL_MEDICATION_GROUPS_OPTIONS;

  ngOnInit(): void {
    this.conversionLengthService.setPatientValidators(
      this.patientDetailAddInfo,
      'neckSize'
    );
    this.patientDetailAddInfo
      .get('isPregnant')
      ?.valueChanges.subscribe((pregnant) => {
        const gaControl = this.patientDetailAddInfo.get('gestationalAge');

        if (!pregnant) {
          // Not pregnant → remove validators and clear value
          gaControl?.clearValidators();
          gaControl?.setValue(null);
        } else {
          // Pregnant → restore validators
          gaControl?.setValidators([
            (control) => {
              const value = control.value;
              if (value == null || value === '') return null;
              const num = Number(value);
              if (isNaN(num)) return { notNumber: true };
              if (num < 2 || num > 43) return { range: true };
              return null;
            },
          ]);
        }

        gaControl?.updateValueAndValidity();
      });
     this.patientDetailAddInfo
      .get('sleepDisorder')
      ?.valueChanges.subscribe((disorder) => {
        const disControl = this.patientDetailAddInfo.get('generic_2');
        if (!disorder) {
          disControl?.clearValidators();
          disControl?.setValue(null);
        } else{
          disControl?.setValidators([Validators.required]);
        }

        disControl?.updateValueAndValidity();
      });

  }

  ngOnChanges() {
    this.setFormValue();
  }
  public patientDetailAddInfo = new FormGroup({
    history: new FormControl('', [
      Validators.minLength(0),
      Validators.maxLength(2048),
    ]),
    medications: new FormControl('', [
      Validators.minLength(0),
      Validators.maxLength(2048),
    ]),

    isPregnant: new FormControl<boolean | null>(null, []),
    gestationalAge: new FormControl('', []),

    medicalCondition: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    generic_1: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    generic_2: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    generic_3: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),

    smoker: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    sleepDisorder: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    race: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    alcohol: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    caffeine: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    exercise: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    stressLevel: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    medicationGroups: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    profession: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    countryOfBirth: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    education: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    incomeLevel: new FormControl('', []),
    neckSize: new FormControl('', []),
    shiftWork: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    orderingPhysicianId: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    referringDoctorId: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    bmi: new FormControl('', [
      Validators.minLength(1),
      Validators.maxLength(256),
    ]),
    insurer: new FormControl('', [Validators.maxLength(50)]),
    shippingAddress: shippingAddressGroup(),
    epworthSleepinessScale: new FormControl(null, [
      Validators.min(0),
      Validators.max(24),
    ]),
  });

  boolChoose = BOOLEAN_CHOOSE;

  sendData() {
    this.validateAllFormFields(this.patientDetailAddInfo);
    // A Direct patient's address is on Main Information, not here.
    if (this.patientType === 'DIRECT') {
      this.additionalFormValue.emit({
        valid: this.patientDetailAddInfo.valid,
        form: 'app-patient-add-info',
        value: { ...this.patientDetailAddInfo.value, shippingAddress: undefined },
      });
      return;
    }
    const valid = this.patientDetailAddInfo.valid;
    // Not awaited: the answer is a warning, never a reason to stay on this step.
    this.checkShippingAddress();
    this.additionalFormValue.emit({
      valid: valid,
      form: 'app-patient-add-info',
      value: {
        ...this.patientDetailAddInfo.value,
        shippingAddress: shippingAddressPayload(this.patientDetailAddInfo.value.shippingAddress),
      },
    });
  }

  get shippingGroup(): FormGroup {
    return this.patientDetailAddInfo.get('shippingAddress') as FormGroup;
  }

  /** Check the shipping address with Easyship — shipping roles only, when the lab has it on. */
  checkShippingAddress(): void {
    this.shippingAddress?.check();
  }

  onFieldChange(
    value: string,
    field: keyof typeof this.patientDetailAddInfo.controls
  ) {
    this.patientDetailAddInfo.patchValue({
      [field]: value,
    });
  }

  onFieldAdd(
    value: string,
    field: keyof typeof this.patientDetailAddInfo.controls
  ) {
    // An empty field holds null/undefined rather than '', so coerce before
    // interpolating, otherwise the appended text is prefixed with "undefined".
    const fieldValue = this.patientDetailAddInfo.value[field] ?? '';
    const newValue = `${fieldValue}
${value}`.trim();
    this.patientDetailAddInfo.patchValue({
      [field]: newValue,
    });
  }


  onReferringDoctorChange(value: string) {
    this.patientDetailAddInfo.get('referringDoctorId')?.setValue(value);
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }
  setFormValue() {
    this.patientDetailAddInfo.patchValue({
      insurer: this.patientData?.insurer,
      shippingAddress: shippingAddressFormValue(this.patientData?.shippingAddress),
      epworthSleepinessScale: this.patientData?.epworthSleepinessScale,
      smoker: this.patientData?.smoker,
      sleepDisorder: this.patientData?.sleepDisorder,
      race: this.patientData?.race,
      alcohol: this.patientData?.alcohol,
      history: this.patientData?.history ?? '',
      medications: this.patientData?.medications ?? '',
      caffeine: this.patientData?.caffeine,
      exercise: this.patientData?.exercise,
      stressLevel: this.patientData?.stressLevel,
      medicationGroups: (this.patientData?.medicationGroups || '').split(','),
      profession: this.patientData?.profession,
      countryOfBirth: this.patientData?.countryOfBirth,
      education: this.patientData?.education,
      incomeLevel: this.patientData?.incomeLevel,
      neckSize: this.patientData?.neckSize,
      shiftWork: this.patientData?.shiftWork,


      orderingPhysicianId: this.patientData?.orderingPhysicianId,
      isPregnant:
        this.patientData?.isPregnant == null
          ? ''
          : this.patientData?.isPregnant,
      gestationalAge: this.patientData?.gestationalAge,

      medicalCondition: this.patientData?.medicalCondition,
      bmi: this.patientData?.bmi,
      generic_1:
        this.patientData?.generic1 == null
          ? ''
          : String(this.patientData?.generic1),
      generic_2:
        this.patientData?.generic2 == null
          ? ''
          : String(this.patientData?.generic2),
      generic_3:
        this.patientData?.generic3 == null
          ? ''
          : String(this.patientData?.generic3),

      referringDoctorId: this.patientData?.referringDoctorId,
    });
  }

  onPhysicianUserChange(item: string) {
    this.patientDetailAddInfo.get('orderingPhysicianId')?.setValue(item);
  }

  onNeckSizeChange(value) {
    this.patientDetailAddInfo.controls['neckSize'].setValue(value);
  }
}
